import TokenizedText from "./TokenizedText";
import TranslationCache from "./TranslationCache";
import { generateUUID } from "./utils";

/**
 * @typedef {Object} TokenApplyOptions
 * Options when applying new token
 *
 * @property {boolean} [dryRun] - Dry run mode
 * @property {boolean} [skipCache] - Do not update cache
 */

/**
 * @typedef {Object} TokenAttributeOptions
 * Options when applying new token to attribute
 *
 * @property {string} language Language code
 * @property {string} [subId] SubId of the translation
 * 
 * @typedef {TokenApplyOptions & TokenAttributeOptions} TokenApplyAttributeOptions
 * Options when applying new token to attribute
 */

/**
 * The translation handle of each HTML node
 * 
 * Also known as the deferer or callback class to handle the translation
 */
class TranslationNode {
    /**
     * Generate a new TranslationNode
     *
     * @param {Element} element HTML main element
     * @param {Element} parentElement The parent element, used if `Node.TEXT_NODE`
     */
    constructor(element, parentElement) {
        /** @type {string} */
        this.id = generateUUID();
        /** @type {Element} */
        this.element = element;
        /** @type {Element | null} */
        this.parentElement = parentElement || element;

        /** @type {TokenizedText[] | undefined} cached tokenized */
        this._cachedTokenized = undefined;

        /** @type {number[]} index with cache already applied */
        this.cachedIndex = [];
        this._blurred = false;

        /**
         * The element that will wrap the text content
         *
         * @type {string}
         */
        this.tagWrapper = 'div';

        /**
         * Extra attributes for the wrapper
         * 
         * @type {{[key: string]: string | string[]}}
         */
        this.extraMetadata = {};

        /**
         * Mark if the current node is mergeable with parent node
         * 
         * @type {boolean}
         */
        this.mergeable = false;
    }

    /**
     * Check if we should translate this text
     *
     * @param {string} text text to check
     * @returns {boolean} should we translate this text
     */
    shouldWeTranslate(text) {
        const trimmed = text.trim();

        // if trimmed is empty, don't translate
        if (trimmed === "") {
            return false;
        }

        // if the text is just a number and maybe comma (including dot), don't translate
        // examples:
        // - 1050 (match)
        // - 10,500,500 (match)
        // - 10.5 (match)
        // - 10,100,500.50 (match)
        if (/^\d+(?:[,.]\d+)*$/.test(trimmed)) {
            return false;
        }

        // skip if text only contains symbols and numbers
        if (/^[^a-zA-Z\p{L}]*$/u.test(trimmed)) {
            return false;
        }

        return true;
    }

    /**
     * Convert/tokenize the element to be translated.
     * 
     * @param {boolean} [ignoreCached=false] Ignore cached tokenized, this will also not save the tokenized to cache
     * @returns {TokenizedText[]} Collection of text to be translated
     */
    tokenize(ignoreCached = false) {
        if (this._cachedTokenized !== undefined && !ignoreCached) {
            return this._cachedTokenized;
        }

        if (this.element.nodeType === Node.TEXT_NODE) {
            const elIndex = Array.from(this.parentElement.childNodes).indexOf(this.element);

            if (elIndex === -1) {
                throw new Error("can't find index of text node");
            }

            if (!this.shouldWeTranslate(this.element.textContent)) {
                return [];
            }

            const data = [
                new TokenizedText(this, elIndex, this.element.textContent),
            ];

            if (!ignoreCached) {
                this._cachedTokenized = data;
            }

            return data;
        }

        // use childNodes to get #text node
        // https://developer.mozilla.org/en-US/docs/Web/API/Node/nodeType
        /** @type {TokenizedText[]} */
        let joinedText = [];
        for (let i = 0; i < this.element.childNodes.length; i++) {
            const node = this.element.childNodes[i];
            if (node.nodeType === Node.TEXT_NODE) {
                if (!this.shouldWeTranslate(node.textContent)) {
                    continue;
                }

                joinedText.push(new TokenizedText(this, i, node.textContent));
            }
        }

        if (!ignoreCached) {
            this._cachedTokenized = joinedText;
        }

        return joinedText;
    }

    /**
     * Make the HTML string that will be sent to the server
     *
     * @returns {string | undefined} HTML string to be translated
     */
    asHtml() {
        const tokenized = this.tokenize();

        // empty, return undefined
        if (!tokenized.length) {
            return undefined;
        }

        let joinedText = [];
        for (let i = 0; i < tokenized.length; i++) {
            const asHTML = tokenized[i].asHtml();
            if (asHTML) {
                joinedText.push(asHTML);
            }
        }
        
        if (!joinedText.length) {
            return undefined;
        }

        return joinedText.join("");
    }

    /**
     * Get and apply single attribute translation
     *
     * @param {string} attrName Attribute name to be changed
     * @param {string} newData The new data to be applied to the attribute
     * @param {TokenApplyAttributeOptions} options The options when applying new token to attribute
     */
    applyAttributeChange(attrName, newData, options) {
        let target = this.element;
        if (target.nodeType === Node.TEXT_NODE) {
            target = this.parentElement;
        }

        const original = target.getAttribute(attrName);
        if (options.dryRun) {
            newData = attrName === 'href' ? `${original}#linguise-translated-demo` : `${original} (translated)`;
        }

        target.setAttribute(attrName, newData);
        if (!options.skipCache && !options.dryRun) {
            TranslationCache.add(original, newData, options.language, options.subId || attrName);
        }
    }

    /**
     * Bind the editor to the element
     * @param {Node} el node to bind
     */
    bindEditor(el) {
        if (window._linguiseEditor) {
            window._linguiseEditor.attach(el);
        }
    }

    /**
     * Unbind the editor from an element
     * @param {Node} el node to unbind
     */
    unbindEditor(el) {
        if (window._linguiseEditor) {
            window._linguiseEditor.detach(el);
        }
    }

    /**
     * Attach editor metadata to the element
     * 
     * @param {TokenizedText} newToken The replacement token
     */
    attachEditor(newToken) {
        const element = this.element.nodeType === Node.TEXT_NODE ? this.parentElement : this.element;

        if (newToken.editorMeta && element) {
            element.setAttribute('data-editor', 'linguise');
            element.setAttribute('data-linguise-hash', newToken.editorMeta.hash);
            element.setAttribute('data-linguise-type', newToken.editorMeta.type);
            if (newToken.editorMeta.altHash) {
                element.setAttribute('data-linguise-alt-hash', newToken.editorMeta.altHash);
            }

            this.bindEditor(element);
        }
    }

    /**
     * Detach editor metadata from the element
     */
    detachEditor() {
        const element = this.element.nodeType === Node.TEXT_NODE ? this.parentElement : this.element;

        if (element) {
            if (this.element.nodeType === Node.TEXT_NODE && element.getAttribute('data-editor') === 'linguise') {
                element.setAttribute('data-linguise-was-edit', '1');
            }
            element.removeAttribute('data-editor');
            element.removeAttribute('data-linguise-hash');
            element.removeAttribute('data-linguise-type');
            element.removeAttribute('data-linguise-alt-hash');

            this.unbindEditor(element);
        }
    }

    /**
     * Replace the text for this element node with the new text
     *
     * @param {TokenizedText} newToken The replacement token
     * @param {string} lang Language code
     * @param {TokenApplyOptions} [options] Options when applying new token
     */
    applyChange(newToken, lang, options) {
        const mergedOptions = Object.assign({}, {
            dryRun: false,
            skipCache: false,
        }, options || {});

        if (!mergedOptions.skipCache) {
            this.cachedIndex.push(newToken.index);
        }

        // after that, we update the DOM, which also will trigger the observer
        // and since we locked it, the lock will be removed and the element can be translated again.
        if (this.element.nodeType === Node.TEXT_NODE) {
            if (mergedOptions.dryRun) {
                const trailingSpaces = this.element.textContent.match(/\s*$/)[0];
                this.element.textContent = newToken.text + " (translated)" + trailingSpaces;
            } else {
                if (!mergedOptions.skipCache) {
                    TranslationCache.add(this.element.textContent, newToken.text, lang, newToken.subId);
                }

                if (newToken.editorMeta) {
                    if (
                        this.parentElement &&
                        this.parentElement.tagName === "SPAN" &&
                        this.parentElement.getAttribute('data-linguise-was-edit') === '1'
                    ) {
                        // Replace the parent metadata
                        this.parentElement.setAttribute('data-editor', 'linguise');
                        this.parentElement.setAttribute('data-linguise-hash', newToken.editorMeta.hash);
                        this.parentElement.setAttribute('data-linguise-editor-wrap', '1');
                        this.parentElement.removeAttribute('data-linguise-was-edit');
        
                        this.element.textContent = newToken.text;
                        this.bindEditor(this.parentElement);
                    } else {
                        // Wrap the text with new span
                        const spanNode = document.createElement('span');
                        spanNode.textContent = newToken.text;
                        spanNode.setAttribute('data-editor', 'linguise');
                        spanNode.setAttribute('data-linguise-hash', newToken.editorMeta.hash);
                        spanNode.setAttribute('data-linguise-editor-wrap', '1');
                        this.bindEditor(spanNode);
        
                        // extractNode is a text node, replace it with the new span node
                        this.element.replaceWith(spanNode);
                    }
                } else {
                    this.element.textContent = newToken.text;
                }
            }
        } else if (this.element.childNodes[newToken.index]) {
            if (mergedOptions.dryRun) {
                const trailingSpaces = this.element.childNodes[newToken.index].textContent.match(/\s*$/)[0];
                this.element.childNodes[newToken.index].textContent = newToken.text + " (translated)" + trailingSpaces;
            } else {
                if (!mergedOptions.skipCache) {
                    TranslationCache.add(this.element.childNodes[newToken.index].textContent, newToken.text, lang, newToken.subId);
                }

                const extractNode = this.element.childNodes[newToken.index];
                if (newToken.editorMeta) {
                    const spanNode = document.createElement('span');
                    spanNode.textContent = newToken.text;
                    spanNode.setAttribute('data-editor', 'linguise');
                    spanNode.setAttribute('data-linguise-hash', newToken.editorMeta.hash);
                    spanNode.setAttribute('data-linguise-editor-wrap', '1');
                    this.bindEditor(spanNode);

                    // extractNode is a text node, replace it with the new span node
                    extractNode.replaceWith(spanNode);
                } else {
                    extractNode.textContent = newToken.text;
                }
            }
        }
    }

    /**
     * Check if the current element is already modified
     *
     * @param {number | null} [index] The token index to be checked, if not provided, it will check all tokens
     * @returns {boolean} Is modified
     */
    isElementModified(index = null) {
        const prevTokenized = this.tokenize();
        const newTokenized = this.tokenize(true);

        // Check single token
        if (typeof index === 'number') {
            const prevToken = prevTokenized.find((token) => token.index === index);
            const newToken = newTokenized.find((token) => token.index === index);

            // Check if the token is not found
            if (!prevToken || !newToken) {
                return true;
            }

            // Check text
            if (prevToken.text !== newToken.text) {
                return true;
            }

            // Check metaExtra
            for (const key in prevToken.metaExtra) {
                if (prevToken.metaExtra[key] !== newToken.metaExtra[key]) {
                    return true;
                }
            }

            return false;
        }

        if (prevTokenized.length !== newTokenized.length) {
            return true;
        }

        // Sort by index
        prevTokenized.sort((a, b) => a.index - b.index);
        newTokenized.sort((a, b) => a.index - b.index);

        // Check all tokens
        for (let i = 0; i < prevTokenized.length; i++) {
            const prevToken = prevTokenized[i];
            const newToken = newTokenized[i];

            // Check text
            if (prevToken.text !== newToken.text) {
                return true;
            }

            // Check metaExtra
            for (const key in prevToken.metaExtra) {
                if (prevToken.metaExtra[key] !== newToken.metaExtra[key]) {
                    return true;
                }
            }
        }

        return false;
    }

    /**
     * Set the parent element as loading for translation.
     */
    blurParent() {
        if (this.parentElement && !this._blurred) {
            this.parentElement.setAttribute("data-linguise-loading", "1");
            this._blurred = true;
        }
    }

    /**
     * Set the parent element as not loading for translation.
     */
    unblurParent() {
        if (this.parentElement) {
            this.parentElement.removeAttribute("data-linguise-loading");
            this._blurred = false;
        }
    }
}

export default TranslationNode;